import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Globe, Check, Search, Loader2 } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";

export const LanguageSelector = () => {
  const { languages, lang, setLang, translating } = useLang();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const ref = useRef(null);

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const current = languages.find((l) => l.code === lang);
  const filtered = languages.filter((l) =>
    l.native.toLowerCase().includes(q.toLowerCase()) || l.name.toLowerCase().includes(q.toLowerCase())
  );

  return (
    <div className="relative" ref={ref}>
      <button
        data-testid="language-selector-button"
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-2 px-3 py-2 rounded-full btn-ghost text-sm font-mono text-zinc-300"
      >
        {translating ? <Loader2 size={16} className="text-cyan animate-spin" /> : <Globe size={16} className="text-cyan" />}
        <span className="hidden sm:inline">{current ? current.native : "EN"}</span>
        <span className="sm:hidden">{current ? current.code.toUpperCase() : "EN"}</span>
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 8, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 8, scale: 0.98 }}
            transition={{ duration: 0.18 }}
            className="absolute right-0 mt-2 w-64 glass rounded-2xl border border-white/10 p-2 z-50 shadow-2xl"
            data-testid="language-dropdown"
          >
            <div className="flex items-center gap-2 px-3 py-2 mb-1 border-b border-white/10">
              <Search size={14} className="text-zinc-500" />
              <input
                data-testid="language-search-input"
                autoFocus
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search language..."
                className="bg-transparent outline-none text-sm text-white w-full placeholder:text-zinc-600 font-mono"
              />
            </div>
            <div className="max-h-72 overflow-y-auto chat-scroll">
              {filtered.map((l) => (
                <button
                  key={l.code}
                  data-testid={`language-option-${l.code}`}
                  onClick={() => { setLang(l.code); setOpen(false); setQ(""); }}
                  className={`w-full flex items-center justify-between gap-2 px-3 py-2 rounded-xl text-left text-sm transition-colors ${lang === l.code ? "bg-[#00F0FF]/10 text-cyan" : "text-zinc-300 hover:bg-white/5"}`}
                >
                  <span className="flex items-center gap-2">
                    <span className="text-base">{l.flag}</span>
                    <span>{l.native}</span>
                  </span>
                  {lang === l.code && <Check size={14} className="text-cyan" />}
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
